import { useState } from 'react'
import { GoogleLogin, CredentialResponse } from '@react-oauth/google'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useQueryClient } from '@tanstack/react-query'
import { API_BASE } from '../lib/api'
import { Logo } from '../components/Logo'
import '../styles/login.css'

export default function LoginPage() {
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const setAuth = useAuthStore(s => s.setAuth)
    const queryClient = useQueryClient()

    const handleGoogleSuccess = async (response: CredentialResponse) => {
        if (!response.credential) {
            setError('No credential received from Google.')
            return
        }

        setLoading(true)
        setError(null)

        try {
            const res = await fetch(API_BASE + '/api/auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ idToken: response.credential })
            })

            if (res.status === 401) {
                setError('Access denied. Only @vpo.ca accounts are allowed.')
                return
            }

            if (res.status === 403) {
                setError('Your account has been deactivated. Contact an administrator.')
                return
            }

            if (!res.ok) {
                setError('Authentication failed. Please try again.')
                return
            }

            const data = await res.json()
            queryClient.clear()
            setAuth(data.user, data.accessToken, data.refreshToken)
            navigate('/')
        } catch {
            setError('Network error. Is the API running?')
        } finally {
            setLoading(false)
        }
    }

    const handleGoogleError = () => {
        setError('Google sign-in was cancelled or failed.')
    }

    return (
        <div className="login-page">
            <div className="login-card">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                    <Logo height={44} />
                </div>
                <p className="subtitle">
                    Retail Intelligence Platform
                </p>

                {error && <div className="login-error">{error}</div>}

                <div className="login-divider">sign in with your work account</div>

                <div className="google-btn-wrapper">
                    <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        theme="filled_black"
                        size="large"
                        width={320}
                        text="signin_with"
                        shape="pill"
                        logo_alignment="left"
                        useOneTap
                    />
                </div>

                <p className="login-domain-note">
                    Restricted to <strong>@vpo.ca</strong> Google Workspace accounts
                </p>

                {loading && (
                    <div style={{ marginTop: 'var(--space-4)', color: 'var(--color-text-secondary)' }}>
                        Authenticating...
                    </div>
                )}
            </div>
        </div>
    )
}
